// skipcq: JS-W1028
import PropTypes from 'prop-types';
import Chip from '@mui/material/Chip';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  formatNormalizedAbv,
  formatSnapshotBeerIssues,
  rawValueCaption,
  snapshotBeerStatus,
  snapshotStatusChipColor,
  snapshotStatusChipLabel,
} from '../../utils/brewery/beerIngestionSnapshot';

const DESCRIPTION_PREVIEW_LENGTH = 120;

export function SnapshotStatusChip({ beer }) {
  if (!beer) {
    return null;
  }
  const status = snapshotBeerStatus(beer);
  return (
    <Chip
      size="small"
      color={snapshotStatusChipColor(status)}
      label={snapshotStatusChipLabel(status)}
    />
  );
}

SnapshotStatusChip.propTypes = {
  beer: PropTypes.object,
};

export function SnapshotFieldCell({ displayValue, caption, emptyLabel = '—' }) {
  return (
    <TableCell>
      <Typography variant="body2">{displayValue || emptyLabel}</Typography>
      {caption ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {caption}
        </Typography>
      ) : null}
    </TableCell>
  );
}

SnapshotFieldCell.propTypes = {
  displayValue: PropTypes.string,
  caption: PropTypes.string,
  emptyLabel: PropTypes.string,
};

export function SnapshotDescriptionCell({ description }) {
  const text = typeof description === 'string' ? description : '';
  if (!text) {
    return (
      <TableCell>
        <Typography variant="body2">—</Typography>
      </TableCell>
    );
  }
  if (text.length <= DESCRIPTION_PREVIEW_LENGTH) {
    return (
      <TableCell>
        <Typography variant="body2">{text}</Typography>
      </TableCell>
    );
  }
  return (
    <TableCell>
      <Tooltip title={text}>
        <Typography variant="body2">{`${text.slice(0, DESCRIPTION_PREVIEW_LENGTH)}…`}</Typography>
      </Tooltip>
    </TableCell>
  );
}

SnapshotDescriptionCell.propTypes = {
  description: PropTypes.string,
};

export function SnapshotIssuesCell({ beer }) {
  const issuesText = beer ? formatSnapshotBeerIssues(beer) : '';
  return (
    <TableCell>
      <Typography variant="body2">{issuesText || '—'}</Typography>
    </TableCell>
  );
}

SnapshotIssuesCell.propTypes = {
  beer: PropTypes.object,
};

export function SnapshotStyleCell({ beer }) {
  const style = beer?.normalized?.style;
  const missingStyle = beer?.issues?.some((issue) => issue.code === 'missing_style');
  return (
    <SnapshotFieldCell
      displayValue={style}
      caption={missingStyle ? 'Not listed' : rawValueCaption(beer?.raw?.style, style)}
    />
  );
}

SnapshotStyleCell.propTypes = {
  beer: PropTypes.object,
};

export function SnapshotAbvCell({ beer }) {
  const abvDisplay = formatNormalizedAbv(beer?.normalized?.abv);
  const missingAbv = beer?.issues?.some((issue) => issue.code === 'missing_abv');
  return (
    <SnapshotFieldCell
      displayValue={abvDisplay}
      caption={missingAbv ? 'Not listed' : rawValueCaption(beer?.raw?.abv, abvDisplay)}
    />
  );
}

SnapshotAbvCell.propTypes = {
  beer: PropTypes.object,
};
